import React, { useState, useEffect, useCallback, useMemo } from "react";
import Notification from "../../components/common/Notification";
import TransactionModal from "./TransactionFormModal";
import "../../assets/styles/common.css";
import "../inventory/Inventory.css";

// const API_URL = process.env.REACT_APP_BACKEND_API_URL;
// const API_URL = "/pharmaapi";
const { API_URL } = require("../../config");

// Reusable Confirmation Modal component
const ConfirmationModal = ({ isOpen, message, onConfirm, onCancel }) => {
  if (!isOpen) return null;
  return (
    <div className="modal-overlay">
      <div className="modal-content small-modal">
        <div className="modal-header">
          <h2>Confirm Voiding</h2>
        </div>
        <div className="modal-body">
          <p>{message}</p>
        </div>
        <div className="modal-actions">
          <button onClick={onConfirm} className="button button-danger">
            Confirm
          </button>
          <button onClick={onCancel} className="button button-secondary">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const Transactions = ({ getAuthHeaders, onLogout, user: loggedInUser }) => {
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // State for the single modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  // The modal mode is now either 'form' or 'view'
  const [modalMode, setModalMode] = useState("form");
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [transactionToVoid, setTransactionToVoid] = useState(null);

  const [allProducts, setAllProducts] = useState([]);

  // Filter states
  const [filterStatus, setFilterStatus] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const sortedTransactions = useMemo(() => {
    let sortableItems = [...transactions];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (sortConfig.key === "user.username") {
          aValue = a.user?.username || "";
          bValue = b.user?.username || "";
        }

        if (aValue < bValue) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [transactions, sortConfig]);

  // Updated filter logic with date range
  const filteredTransactions = useMemo(() => {
    let tempTransactions = [...sortedTransactions];

    // Filter by status
    if (filterStatus === "voided") {
      tempTransactions = tempTransactions.filter(
        (transaction) => transaction.status === "voided"
      );
    } else if (filterStatus === "active") {
      tempTransactions = tempTransactions.filter(
        (transaction) => transaction.status === "active"
      );
    }

    // Filter by date rangeendDate
    if (startDate) {
      tempTransactions = tempTransactions.filter(
        (transaction) => new Date(transaction.createdAt) >= new Date(startDate)
      );
    }
    if (endDate) {
      // Create a Date object for the day AFTER the endDate
      const dayAfterEndDate = new Date(endDate);
      dayAfterEndDate.setDate(dayAfterEndDate.getDate() + 1);
      
      // Filter transactions that occurred BEFORE the start of the next day
      tempTransactions = tempTransactions.filter(
        (transaction) => new Date(transaction.createdAt) < dayAfterEndDate
      );
    }

    return tempTransactions;
  }, [sortedTransactions, filterStatus, startDate, endDate]);

  // Reset to first page whenever filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus, startDate, endDate]);

  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
  const paginatedTransactions = filteredTransactions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleOpenFormModal = () => {
    setModalMode("form");
    setIsModalOpen(true);
    setSelectedTransaction(null);
    setNotification(null);
  };

  const handleViewTransaction = (transaction) => {
    setSelectedTransaction(transaction);
    setModalMode("view");
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTransaction(null);
    setNotification(null);
  };

  const fetchTransactions = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/transactions`, {
        headers: getAuthHeaders(),
      });

      if (response.status === 401) {
        onLogout();
        return;
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch transactions: ${response.statusText}`);
      }
      const data = await response.json();
      setTransactions(data);
    } catch (err) {
      console.error("Error fetching transactions:", err);
      setError(err.message || "Failed to load transactions.");
    } finally {
      setIsLoading(false);
    }
  }, [getAuthHeaders, onLogout]);

  const fetchAllProducts = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/products`, {
        headers: getAuthHeaders(),
      });

      if (response.status === 401) {
        onLogout();
        return;
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch products: ${response.statusText}`);
      }
      const data = await response.json();
      setAllProducts(data);
    } catch (err) {
      console.error("Error fetching all products:", err);
    }
  }, [getAuthHeaders, onLogout]);

  useEffect(() => {
    fetchTransactions();
    fetchAllProducts();
  }, [fetchTransactions, fetchAllProducts]);

  // handleSaveTransaction is now only for creating new transactions
  const handleSaveTransaction = async (id, transactionData) => {
    setIsLoading(true);
    setError(null);
    setNotification(null);

    const errorMessage = "An error occurred while creating transaction.";
    const transactionDataWithUserId = {
      ...transactionData,
      userId: loggedInUser?.id,
    };

    try {
      const response = await fetch(`${API_URL}/transactions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        body: JSON.stringify(transactionDataWithUserId),
      });

      if (response.status === 401) {
        onLogout();
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorMessage);
      }

      await response.json();
      setNotification({
        message: "Transaction created successfully!",
        type: "success",
      });
      handleCloseModal();
      fetchTransactions();
      fetchAllProducts(); // Re-fetch products to update stock in autocomplete
    } catch (err) {
      console.error("Error creating transaction:", err);
      setNotification({
        message: err.message || errorMessage,
        type: "error",
      });
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // NEW: Function to handle voiding a transaction
  const handleVoidTransaction = useCallback(async (id) => {
    setShowConfirmationModal(true);
    setTransactionToVoid(id);
  }, []);

  const confirmVoidTransaction = async () => {
    setShowConfirmationModal(false);
    if (!transactionToVoid) return;

    setIsLoading(true);
    setError(null);
    setNotification(null);

    try {
      // Assuming a specific endpoint for voiding a transaction
      // This will update the transaction status and reverse inventory
      const response = await fetch(
        `${API_URL}/transactions/${transactionToVoid}/void`,
        {
          method: "PATCH",
          headers: getAuthHeaders(),
        }
      );

      if (response.status === 401) {
        onLogout();
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message ||
            "An error occurred while voiding the transaction."
        );
      }

      await response.json();
      setNotification({
        message: "Transaction voided successfully!",
        type: "success",
      });
      handleCloseModal();
      fetchTransactions();
      fetchAllProducts(); // ADDED: Re-fetch products to update stock levels
    } catch (err) {
      console.error("Error voiding transaction:", err);
      setNotification({
        message: err.message || "Failed to void transaction.",
        type: "error",
      });
      setError(err.message);
    } finally {
      setTransactionToVoid(null);
      setIsLoading(false);
    }
  };

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  if (isLoading) {
    return <p className="loading-message">Loading transactions...</p>;
  }

  if (error) {
    return <p className="error-message">{error}</p>;
  }

  return (
    <div className="settings-content-section">
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
      <div className="settings-header-actions">
        <h2>Transactions</h2>
        <div className="global-action-buttons">
          <button onClick={handleOpenFormModal} className="btn-primary">
            <span className="button-icon">&#x2795;</span> Add New Transaction
          </button>
        </div>
      </div>

      <TransactionModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        mode={modalMode}
        transaction={selectedTransaction}
        products={allProducts}
        onSubmit={handleSaveTransaction}
        onVoid={handleVoidTransaction} // Pass the new void handler
      />

      <ConfirmationModal
        isOpen={showConfirmationModal}
        message="Are you sure you want to void this transaction? This action cannot be undone."
        onConfirm={confirmVoidTransaction}
        onCancel={() => {
          setShowConfirmationModal(false);
          setTransactionToVoid(null);
        }}
      />

      <div style={{ marginTop: "20px" }}>
        <h3>All Transactions</h3>
        {/* Updated filter controls to be in a single line with improved spacing and font */}
        <div className="filter-controls-container-inline">
          <div className="filter-group-inline">
            <label htmlFor="status-filter">Status: </label>
            <select
              id="status-filter"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="filter-select-inline"
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="voided">Voided</option>
            </select>
          </div>
          <div className="filter-group-inline">
            <label htmlFor="start-date-filter">From: </label>
            <input
              type="date"
              id="start-date-filter"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="filter-input-inline"
            />
          </div>
          <div className="filter-group-inline">
            <label htmlFor="end-date-filter">To: </label>
            <input
              type="date"
              id="end-date-filter"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="filter-input-inline"
            />
          </div>
        </div>
        {filteredTransactions.length > 0 ? (
          <>
            <table className="user-table">
              <thead>
                <tr>
                  <th onClick={() => requestSort("id")}>
                    ID
                    {sortConfig.key === "id" && (
                      <span>
                        {sortConfig.direction === "ascending" ? " ▲" : " ▼"}
                      </span>
                    )}
                  </th>
                  <th onClick={() => requestSort("user.username")}>
                    User
                    {sortConfig.key === "user.username" && (
                      <span>
                        {sortConfig.direction === "ascending" ? " ▲" : " ▼"}
                      </span>
                    )}
                  </th>
                  <th onClick={() => requestSort("type")}>
                    Type
                    {sortConfig.key === "type" && (
                      <span>
                        {sortConfig.direction === "ascending" ? " ▲" : " ▼"}
                      </span>
                    )}
                  </th>
                  <th onClick={() => requestSort("totalAmount")}>
                    Total Amount
                    {sortConfig.key === "totalAmount" && (
                      <span>
                        {sortConfig.direction === "ascending" ? " ▲" : " ▼"}
                      </span>
                    )}
                  </th>
                  <th onClick={() => requestSort("createdAt")}>
                    Date
                    {sortConfig.key === "createdAt" && (
                      <span>
                        {sortConfig.direction === "ascending" ? " ▲" : " ▼"}
                      </span>
                    )}
                  </th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {paginatedTransactions.map((transaction) => (
                  <tr
                    key={transaction.id}
                    onDoubleClick={() => handleViewTransaction(transaction)}
                    // Conditional class for voided transactions
                    className={
                      transaction.status === "voided"
                        ? "voided-transaction"
                        : ""
                    }
                  >
                    <td>
                      #{transaction.id ? transaction.id.substring(0, 8) : "N/A"}
                    </td>
                    <td>{transaction.user?.username || "N/A"}</td>
                    <td>{transaction.type}</td>
                    <td>
                      ₱{parseFloat(transaction.totalAmount || 0).toFixed(2)}
                    </td>
                    <td>
                      {new Date(transaction.createdAt).toLocaleString("en-US", {
                        year: "numeric",
                        month: "numeric",
                        day: "numeric",
                        hour: "numeric",
                        minute: "numeric",
                        second: "numeric",
                        hour12: true,
                      })}
                    </td>
                    <td>{transaction.notes || "N/A"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                  className="pagination-button"
                >
                  Previous
                </button>
                {[...Array(totalPages).keys()].map((number) => (
                  <button
                    key={number + 1}
                    onClick={() => paginate(number + 1)}
                    className={`pagination-button ${
                      currentPage === number + 1 ? "active" : ""
                    }`}
                  >
                    {number + 1}
                  </button>
                ))}
                <button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  className="pagination-button"
                >
                  Next
                </button>
              </div>
            )}
          </>
        ) : (
          <p className="no-data">No transactions found.</p>
        )}
      </div>
    </div>
  );
};

export default Transactions;
